import { Box, Stack, Typography } from '@mui/material';
import { GroupType } from '@schooly/api';
import { getGroupTypeTestIdByValue } from '@schooly/constants/test-ids';
import { GroupsIcon, RadioGroupCard, TagSimple } from '@schooly/style';
import { FC } from 'react';
import { useIntl } from 'react-intl';

export type GroupTypeSelectProps = {
  selectedType?: GroupType;
  onSelectType?: (type: GroupType) => void;
};

const GROUP_TYPES = [GroupType.TutorGroup, GroupType.Subject, GroupType.NoSubject];
const noop = () => {};

export const GroupTypeSelect: FC<GroupTypeSelectProps> = ({ selectedType, onSelectType }) => {
  const { formatMessage } = useIntl();
  return (
    <Stack gap={1} my={1}>
      <Typography variant="h2">
        {formatMessage({ id: 'groups-GroupTypeSelect-Question' })}
      </Typography>
      {selectedType === GroupType.NoSubject && (
        <Typography variant="h3" color="text.primary">
          {formatMessage({
            id: 'groups-GroupTypeSelect-AssessmentNote',
          })}
        </Typography>
      )}
      <Stack gap={1} flexDirection="row">
        {GROUP_TYPES.map((type) => (
          <Box key={type} data-test-id={getGroupTypeTestIdByValue(type)} sx={{ flex: 1 }}>
            <GroupTypeSelectCard
              type={type}
              isSelected={selectedType === type}
              disabled={!onSelectType}
              onSelect={onSelectType ?? noop}
            />
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

type GroupTypeSelectCardProps = {
  type: GroupType;
  isSelected: boolean;
  disabled?: boolean;
  onSelect: (type: GroupType) => void;
};

const GroupTypeSelectCard: FC<GroupTypeSelectCardProps> = ({
  type,
  isSelected,
  disabled,
  onSelect,
}) => {
  const { formatMessage } = useIntl();

  const { labelTextId, description, groupName, subjectName } = getGroupTypeSelectCardProps(
    type,
    formatMessage,
  );

  const primaryColorName = isSelected ? 'primary.main' : 'common.grey';
  const secondaryColorName = isSelected ? 'text.primary' : 'common.grey';

  return (
    <RadioGroupCard
      labelTextId={labelTextId}
      checked={isSelected}
      disabled={disabled}
      controlTooltipContent={
        disabled && !isSelected
          ? formatMessage({ id: 'groups-GroupTypeSelect-CannotChangeType' })
          : undefined
      }
      sx={
        disabled && !isSelected
          ? (theme) => ({
              backgroundColor:
                type === GroupType.Subject ? theme.palette.background.default : undefined,

              '& .Mui-disabled.MuiFormControlLabel-root .MuiFormControlLabel-label': {
                color: theme.palette.common.grey,
              },
              ' .svg-icon circle:first-of-type:first-of-type': {
                '&:first-of-type': {
                  stroke: theme.palette.common.main5,
                },
              },
            })
          : undefined
      }
      onChange={() => onSelect(type)}
    >
      <Stack flex={1} justifyContent="space-between" gap={1} pb={1.5}>
        <Typography color={primaryColorName}>{description}</Typography>
        <Stack gap={1.5}>
          <Typography color={secondaryColorName}>
            {formatMessage({ id: 'groups-GroupTypeSelect-Eg' })}
          </Typography>
          <Stack
            sx={(theme) => ({
              gap: theme.spacing(1),
              alignItems: 'flex-start',
              padding: theme.spacing(1.5),
              borderRadius: theme.spacing(1),
              boxShadow: '0px 10px 20px rgba(37, 39, 90, 0.05)',
              backgroundColor: theme.palette.background.paper,
            })}
          >
            <Typography variant="h3" color={primaryColorName}>
              <Box sx={{ position: 'relative', top: -1, display: 'inline-block', mr: 0.5 }}>
                <GroupsIcon />
              </Box>
              {groupName}
            </Typography>
            <TagSimple variant="outlined" sx={{ color: primaryColorName }}>
              {subjectName}
            </TagSimple>
          </Stack>
        </Stack>
      </Stack>
    </RadioGroupCard>
  );
};

const getGroupTypeSelectCardProps = (
  type: GroupType,
  formatMessage: (descriptor: { id: string }) => string,
) => {
  switch (type) {
    case GroupType.Subject:
      return {
        labelTextId: 'groups-GroupTypeSelect-Subject-title',
        description: formatMessage({ id: 'groups-GroupTypeSelect-Subject-description' }),
        groupName: formatMessage({ id: 'groups-GroupTypeSelect-Subject-groupName' }),
        subjectName: formatMessage({ id: 'groups-GroupTypeSelect-Subject-subjectName' }),
      };
    case GroupType.TutorGroup:
      return {
        labelTextId: 'groups-TutorGroup',
        description: formatMessage({ id: 'groups-GroupTypeSelect-TutorGroup-description' }),
        groupName: formatMessage({ id: 'groups-GroupTypeSelect-TutorGroup-groupName' }),
        subjectName: formatMessage({ id: 'groups-TutorGroup' }),
      };
    case GroupType.NoSubject:
      return {
        labelTextId: 'groups-NoSubject',
        description: formatMessage({ id: 'groups-GroupTypeSelect-NoSubject-description' }),
        groupName: formatMessage({ id: 'groups-GroupTypeSelect-NoSubject-groupName' }),
        subjectName: formatMessage({ id: 'groups-NoSubject' }),
      };
  }
};
